import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  getDocs,
  writeBatch,
  limit,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { inputClass, labelClass } from "./recurringRules/styles";
import StatPill from "./recurringRules/StatPill";
import SectionCard from "./recurringRules/SectionCard";
import {
  ASSIGNMENT_COLLECTION_CANDIDATES,
  RULE_LINK_FIELD_CANDIDATES,
  ASSIGNMENT_DATE_FIELD_CANDIDATES,
  isYYYYMMDD,
  compareYYYYMMDD,
  todayYYYYMMDD,
  maxYYYYMMDD,
  pickFirstExistingField,
  isNotCompletedAssignment,
  fmtDate,
  getRuleCompanyIds,
  getRuleTemplateCounts,
  getRuleDriverId,
  getRuleDriverEmail,
  buildAssignmentDriverPatch,
  patchNestedRunsIfAny,
  buildAssignmentDocIdLike,
} from "./recurringRules/helpers";

/**
 * IMPORTANT (why driver didn't see the reassigned run):
 * DriverDashboard reads assignments by DOCUMENT ID: assignments/{driverUid}_{date}
 * So reassign MUST "move/rename" the assignment doc ID to the new driver UID.
 */

export default function RecurringRulesTab({ companies, drivers }) {
  const [companyId, setCompanyId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [allRules, setAllRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [driverPickByRule, setDriverPickByRule] = useState({});
  const [busyRuleId, setBusyRuleId] = useState("");
  const [openRuleIds, setOpenRuleIds] = useState(() => new Set());

  const toggleRuleOpen = (ruleId) => {
    setOpenRuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  const driverMap = useMemo(() => {
    const map = {};
    (drivers || []).forEach((d) => (map[d.uid] = d.email || d.displayName || d.uid));
    return map;
  }, [drivers]);

  const driverEmailToUid = useMemo(() => {
    const map = {};
    (drivers || []).forEach((d) => {
      if (d.email) map[String(d.email).toLowerCase()] = d.uid;
    });
    return map;
  }, [drivers]);

  const uidToDriver = useMemo(() => {
    const map = {};
    (drivers || []).forEach((d) => (map[d.uid] = d));
    return map;
  }, [drivers]);

  const companyMap = useMemo(() => {
    const map = {};
    (companies || []).forEach((c) => (map[c.id] = c.name || c.id));
    return map;
  }, [companies]);

  useEffect(() => {
    setLoading(true);
    const base = collection(db, "recurrenceRules");
    const q = query(base, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllRules(list);
        setLoading(false);

        setDriverPickByRule((prev) => {
          const next = { ...prev };
          list.forEach((r) => {
            if (next[r.id]) return;

            const uid = getRuleDriverId(r);
            if (uid) next[r.id] = uid;

            const email = getRuleDriverEmail(r);
            if (!uid && email) {
              const mapped = driverEmailToUid[String(email).toLowerCase()];
              if (mapped) next[r.id] = mapped;
            }
          });
          return next;
        });
      },
      (err) => {
        console.error("Failed to load recurrenceRules:", err);
        setAllRules([]);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [driverEmailToUid]);

  const filteredRules = useMemo(() => {
    if (!allRules.length) return [];
    return allRules.filter((rule) => {
      if (companyId) {
        const ids = getRuleCompanyIds(rule);
        if (!ids.includes(companyId)) return false;
      }
      if (driverId) {
        const rUid = getRuleDriverId(rule);
        if (rUid) return rUid === driverId;

        const email = getRuleDriverEmail(rule);
        if (email) {
          const mappedUid = driverEmailToUid[String(email).toLowerCase()];
          return mappedUid === driverId;
        }
        return false;
      }
      return true;
    });
  }, [allRules, companyId, driverId, driverEmailToUid]);

  const diagnostics = useMemo(() => {
    let missingCompany = 0;
    let missingDriver = 0;
    allRules.forEach((r) => {
      const ids = getRuleCompanyIds(r);
      if (!ids.length) missingCompany += 1;

      const hasDriverUid = !!getRuleDriverId(r);
      const hasDriverEmail = !!getRuleDriverEmail(r);
      if (!hasDriverUid && !hasDriverEmail) missingDriver += 1;
    });
    return { missingCompany, missingDriver };
  }, [allRules]);

  const queryStatus = useMemo(() => {
    const parts = [];
    parts.push(companyId ? `Company: ${companyMap[companyId] || companyId}` : "Company: All");
    parts.push(driverId ? `Driver: ${driverMap[driverId] || driverId}` : "Driver: All");
    return parts.join(" • ");
  }, [companyId, driverId, companyMap, driverMap]);

  const totalRules = filteredRules.length;
  const activeRules = filteredRules.filter((r) => r.active && !r.endedAt).length;
  const endedRules = filteredRules.filter((r) => !!r.endedAt).length;

  const toggleActive = async (rule) => {
    try {
      await updateDoc(doc(db, "recurrenceRules", rule.id), {
        active: !rule.active,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      alert("Failed to update rule.");
    }
  };

  const endRule = async (rule) => {
    const ok = window.confirm(
      "End & delete this recurring rule?\n\n" +
        "✅ It will STOP creating future runs\n" +
        "✅ Existing assignments will stay\n" +
        "🗑️ The rule will be DELETED and disappear from this list\n\n" +
        "This cannot be undone.",
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "recurrenceRules", rule.id));
    } catch (e) {
      console.error(e);
      alert("Failed to delete rule.");
    }
  };

  const setCurrentDate = async (rule) => {
    const next = prompt("Set currentDate (YYYY-MM-DD)", rule.currentDate || rule.startDate || "");
    if (!next) return;
    if (!isYYYYMMDD(next)) return alert("Invalid format. Use YYYY-MM-DD.");
    try {
      await updateDoc(doc(db, "recurrenceRules", rule.id), {
        currentDate: next,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      alert("Failed to update currentDate.");
    }
  };

  async function detectAssignmentStorage(ruleId) {
    for (const colName of ASSIGNMENT_COLLECTION_CANDIDATES) {
      const colRef = collection(db, colName);

      for (const linkField of RULE_LINK_FIELD_CANDIDATES) {
        try {
          const q1 = query(colRef, where(linkField, "==", ruleId), limit(1));
          const snap = await getDocs(q1);
          if (!snap.empty) return { colName, linkField };
        } catch {
          // ignore
        }
      }
    }
    return { colName: "", linkField: "" };
  }

  async function reassignAssignmentsForRule({
    ruleId,
    newDriverUid,
    newDriverEmail,
    cutoffDate,
    untilDate,
  }) {
    const { colName, linkField } = await detectAssignmentStorage(ruleId);
    if (!colName || !linkField) {
      throw new Error(
        `Can't find assignment docs for this rule.\n` +
          `Tried collections: ${ASSIGNMENT_COLLECTION_CANDIDATES.join(", ")}\n` +
          `Tried fields: ${RULE_LINK_FIELD_CANDIDATES.join(", ")}`,
      );
    }

    const colRef = collection(db, colName);
    const snap = await getDocs(query(colRef, where(linkField, "==", ruleId), limit(2000)));

    const basePatch = buildAssignmentDriverPatch({ newDriverUid, newDriverEmail });

    let updated = 0;
    let renamed = 0;
    let skippedNoDate = 0;
    let skippedPast = 0;
    let skippedCompleted = 0;
    let batch = writeBatch(db);
    let ops = 0;

    for (const d of snap.docs) {
      const data = d.data();

      const dateField = pickFirstExistingField(data, ASSIGNMENT_DATE_FIELD_CANDIDATES);
      if (!dateField) {
        skippedNoDate += 1;
        continue;
      }

      const adate = String(data[dateField] || "");
      if (!isYYYYMMDD(adate)) {
        skippedNoDate += 1;
        continue;
      }

      if (compareYYYYMMDD(adate, cutoffDate) === -1) {
        skippedPast += 1;
        continue;
      }

      if (!isNotCompletedAssignment(data)) {
        skippedCompleted += 1;
        continue;
      }

      if (untilDate && compareYYYYMMDD(adate, untilDate) === 1) {
        continue;
      }

      const nestedPatch = patchNestedRunsIfAny(data, newDriverUid, newDriverEmail);
      const finalPatch = nestedPatch ? { ...basePatch, ...nestedPatch } : basePatch;

      const newId = buildAssignmentDocIdLike(d.id, newDriverUid);

      if (newId && newId !== d.id) {
        batch.set(doc(db, colName, newId), { ...data, ...finalPatch }, { merge: true });
        batch.delete(d.ref);
        renamed += 1;
        updated += 1;
        ops += 2;
      } else {
        batch.update(d.ref, finalPatch);
        updated += 1;
        ops += 1;
      }

      if (ops >= 430) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }

    if (ops > 0) await batch.commit();

    return {
      updated,
      renamed,
      skippedNoDate,
      skippedPast,
      skippedCompleted,
      colName,
      linkField,
    };
  }

  const applyDriverTemporary = async (rule) => {
    const newUid = driverPickByRule[rule.id] || "";
    if (!newUid) return alert("Please select a driver first.");

    const newDriver = uidToDriver[newUid];
    const newEmail = newDriver?.email || "";

    const until = prompt(
      "Temporary reassignment until what date? (YYYY-MM-DD)\n\n" +
        "✅ Updates NOT-completed assignments\n" +
        "✅ Renames assignment docs so the new driver sees them\n" +
        "✅ Does NOT change the rule default driver\n\n" +
        "Leave blank to apply to ALL future not-completed assignments.",
      "",
    );

    let untilDate = "";
    if (until && String(until).trim()) {
      const v = String(until).trim();
      if (!isYYYYMMDD(v)) return alert("Invalid date. Use YYYY-MM-DD.");
      untilDate = v;
    }

    const cutoffDate = maxYYYYMMDD(todayYYYYMMDD(), rule.currentDate || "");

    const ok = window.confirm(
      `Temporary reassignment\n\nRule: ${rule.name || rule.id}\n` +
        `New driver: ${newEmail || newUid}\n` +
        `Cutoff (protect past): ${cutoffDate}\n` +
        `Until: ${untilDate || "(ALL future not-completed)"}\n\n` +
        `This will rename assignment docs like {driverUid}_{date} so the new driver can see the run.`,
    );
    if (!ok) return;

    setBusyRuleId(rule.id);
    try {
      const res = await reassignAssignmentsForRule({
        ruleId: rule.id,
        newDriverUid: newUid,
        newDriverEmail: newEmail,
        cutoffDate,
        untilDate: untilDate || undefined,
      });

      alert(
        `Done.\n\n` +
          `Updated: ${res.updated}\n` +
          `Renamed (driver docId moved): ${res.renamed}\n\n` +
          `Skipped past (cutoff): ${res.skippedPast}\n` +
          `Skipped completed: ${res.skippedCompleted}\n` +
          `Skipped missing/invalid date: ${res.skippedNoDate}\n\n` +
          `Collection: ${res.colName}\nLink field: ${res.linkField}\n\n` +
          `Now refresh the Driver page for the new driver.`,
      );
    } catch (e) {
      console.error(e);
      alert(`Failed.\n\n${e?.message || "Check console for details."}`);
    } finally {
      setBusyRuleId("");
    }
  };

  const applyDriverAsDefault = async (rule) => {
    const newUid = driverPickByRule[rule.id] || "";
    if (!newUid) return alert("Please select a driver first.");

    const newDriver = uidToDriver[newUid];
    const newEmail = newDriver?.email || "";
    const cutoffDate = maxYYYYMMDD(todayYYYYMMDD(), rule.currentDate || "");

    const ok = window.confirm(
      `Change default driver\n\nRule: ${rule.name || rule.id}\n` +
        `New default driver: ${newEmail || newUid}\n` +
        `Cutoff (protect past): ${cutoffDate}\n\n` +
        `This will:\n` +
        `1) Update the rule default driver\n` +
        `2) Update ONLY future (date >= cutoff) NOT-completed assignment docs\n` +
        `3) Rename {driverUid}_{date} docs so the new driver can see them\n\n` +
        `Past runs will NOT be changed.`,
    );
    if (!ok) return;

    setBusyRuleId(rule.id);
    try {
      await updateDoc(doc(db, "recurrenceRules", rule.id), {
        driverId: newUid,
        driverUid: newUid,
        driverEmail: newEmail || null,
        updatedAt: serverTimestamp(),
      });

      const res = await reassignAssignmentsForRule({
        ruleId: rule.id,
        newDriverUid: newUid,
        newDriverEmail: newEmail,
        cutoffDate,
      });

      alert(
        `Done.\n\n` +
          `Rule default driver updated.\n\n` +
          `Updated: ${res.updated}\n` +
          `Renamed (driver docId moved): ${res.renamed}\n\n` +
          `Skipped past (cutoff): ${res.skippedPast}\n` +
          `Skipped completed: ${res.skippedCompleted}\n` +
          `Skipped missing/invalid date: ${res.skippedNoDate}\n\n` +
          `Collection: ${res.colName}\nLink field: ${res.linkField}`,
      );
    } catch (e) {
      console.error(e);
      alert(`Failed.\n\n${e?.message || "Check console for details."}`);
    } finally {
      setBusyRuleId("");
    }
  };

  return (
    <div className="space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{totalRules}</span> rules
          {" · "}
          <span className="font-semibold text-emerald-700">{activeRules}</span> active
          {" · "}
          {endedRules} ended
          {(diagnostics.missingCompany > 0 || diagnostics.missingDriver > 0) && (
            <>
              {" · "}
              <span className="font-medium text-amber-700">
                Missing company: {diagnostics.missingCompany}, missing driver: {diagnostics.missingDriver}
              </span>
            </>
          )}
        </div>
        <div className="mt-1 text-sm text-gray-500">{queryStatus}</div>
        {!!allRules.length && (
          <div className="mt-0.5 text-[11px] text-gray-400">
            Loaded {allRules.length} rule{allRules.length !== 1 ? "s" : ""} from recurrenceRules.
          </div>
        )}
      </div>

      <SectionCard
        title="Filters"
        subtitle="Use filters to narrow recurring rules by company or default driver."
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[1fr_1fr_auto]">
          <div className="flex flex-col gap-1.5">
            <span className={labelClass}>Company</span>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className={inputClass}
            >
              <option value="">All companies</option>
              {(companies || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={labelClass}>Driver</span>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              className={inputClass}
            >
              <option value="">All drivers</option>
              {(drivers || []).map((d) => (
                <option key={d.uid} value={d.uid}>
                  {d.email || d.uid}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setCompanyId("");
                setDriverId("");
              }}
              className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Reset filters
            </button>
          </div>
        </div>
      </SectionCard>

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
          Loading recurring rules...
        </div>
      ) : !filteredRules.length ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
          No recurring rules found for the selected filters.
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRules.map((rule) => {
            const { runCount, stopCount } = getRuleTemplateCounts(rule);
            const isEnded = !!rule.endedAt;

            const companyIds = getRuleCompanyIds(rule);
            const isMultiCompany = companyIds.length > 1;

            const rDriverUid = getRuleDriverId(rule);
            const rDriverEmail = getRuleDriverEmail(rule);
            const driverLabel =
              (rDriverUid && driverMap[rDriverUid]) ||
              (rDriverEmail ? rDriverEmail : "") ||
              "-";

            const pickedUid = driverPickByRule[rule.id] || "";
            const pickedLabel = pickedUid ? driverMap[pickedUid] || pickedUid : "";
            const isBusy = busyRuleId === rule.id;
            const ruleIsOpen = openRuleIds.has(rule.id);

            return (
              <div
                key={rule.id}
                className="overflow-hidden rounded-[26px] border border-gray-200 bg-white shadow-sm"
              >
                <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-950">
                          {rule.name || "Recurring Rule"}
                        </h3>
                        <StatPill tone={rule.active && !isEnded ? "emerald" : "gray"}>
                          {rule.active && !isEnded ? "Active" : "Inactive"}
                        </StatPill>
                        {isEnded && <StatPill tone="red">Ended</StatPill>}
                        {isMultiCompany && <StatPill tone="indigo">Multi-company</StatPill>}
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                            Default driver
                          </div>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {driverLabel}
                          </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                            Frequency
                          </div>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {rule.frequency || "-"}
                          </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                            Template
                          </div>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {runCount} run{runCount !== 1 ? "s" : ""} • {stopCount} stop{stopCount !== 1 ? "s" : ""}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => toggleRuleOpen(rule.id)}
                        aria-expanded={ruleIsOpen}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                      >
                        {ruleIsOpen ? "Hide details" : "Details & reassign"}
                        <svg
                          className={`h-3.5 w-3.5 transition-transform ${ruleIsOpen ? "rotate-180" : ""}`}
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <button
                        className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        onClick={() => setCurrentDate(rule)}
                      >
                        Set current date
                      </button>

                      <button
                        className={`rounded-xl border px-3.5 py-2 text-sm font-medium transition ${
                          rule.active && !isEnded
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => toggleActive(rule)}
                        disabled={isEnded}
                      >
                        {rule.active && !isEnded ? "Disable" : "Enable"}
                      </button>

                      <button
                        className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
                        onClick={() => endRule(rule)}
                      >
                        End & delete rule
                      </button>
                    </div>
                  </div>
                </div>

                {ruleIsOpen && (
                <div className="space-y-4 px-5 py-5 sm:px-6">
                  <SectionCard
                    title="Rule details"
                    subtitle="Overview of scheduling, company scope, and stored identifiers."
                    right={<StatPill tone="gray">Rule ID: {rule.id}</StatPill>}
                  >
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-3 text-sm text-gray-700">
                        <div>
                          <span className="font-medium text-gray-500">Company:</span>{" "}
                          {companyIds.length === 0 ? (
                            <span className="text-red-600">(missing)</span>
                          ) : companyIds.length === 1 ? (
                            companyMap[companyIds[0]] || companyIds[0]
                          ) : (
                            <span className="inline-flex flex-wrap gap-2">
                              {companyIds.map((cid) => (
                                <span
                                  key={cid}
                                  className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700"
                                >
                                  {companyMap[cid] || cid}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>

                        <div>
                          <span className="font-medium text-gray-500">Date range:</span>{" "}
                          {fmtDate(rule.startDate)}
                          {rule.endDate ? ` → ${fmtDate(rule.endDate)}` : " → (no end)"}
                        </div>

                        <div>
                          <span className="font-medium text-gray-500">Current date:</span>{" "}
                          {fmtDate(rule.currentDate) || "-"}
                        </div>
                      </div>

                      <div className="space-y-3 text-sm text-gray-700">
                        <div>
                          <span className="font-medium text-gray-500">Driver UID:</span>{" "}
                          <span className="font-mono text-xs">{rDriverUid || "-"}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Driver email:</span>{" "}
                          {rDriverEmail || "-"}
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Created from:</span>{" "}
                          {rule.createdFrom || "-"}
                        </div>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Reassign driver"
                    subtitle="Update only future not-completed assignments. Driver-based assignment doc IDs will be renamed so the new driver can see the runs."
                    right={isBusy ? <StatPill tone="amber">Working...</StatPill> : null}
                  >
                    <div className="grid gap-4 xl:grid-cols-[1fr_auto]">
                      <div className="space-y-4">
                        <div className="flex flex-col gap-1.5">
                          <span className={labelClass}>Select driver</span>
                          <select
                            value={pickedUid}
                            onChange={(e) =>
                              setDriverPickByRule((prev) => ({
                                ...prev,
                                [rule.id]: e.target.value,
                              }))
                            }
                            className={inputClass}
                            disabled={isBusy}
                          >
                            <option value="">-- Select driver --</option>
                            {(drivers || []).map((d) => (
                              <option key={d.uid} value={d.uid}>
                                {d.email || d.uid}
                              </option>
                            ))}
                          </select>

                          {!!pickedLabel && (
                            <div className="text-xs text-gray-500">
                              Selected:{" "}
                              <span className="font-medium text-gray-700">{pickedLabel}</span>
                            </div>
                          )}
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-6 text-amber-800">
                          Temporary change updates the future runs only.
                          <br />
                          Change default driver also updates the rule itself for future generation.
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 xl:min-w-[230px]">
                        <button
                          className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
                            isBusy
                              ? "border-gray-200 bg-white text-gray-400"
                              : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                          onClick={() => applyDriverTemporary(rule)}
                          disabled={isBusy || isEnded}
                        >
                          {isBusy ? "Working..." : "Temporary (runs only)"}
                        </button>

                        <button
                          className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
                            isBusy
                              ? "border-emerald-200 bg-emerald-50 text-emerald-300"
                              : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          }`}
                          onClick={() => applyDriverAsDefault(rule)}
                          disabled={isBusy || isEnded}
                        >
                          {isBusy ? "Working..." : "Change default driver"}
                        </button>
                      </div>
                    </div>
                  </SectionCard>
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}